import React, { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useParams, Link } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Calendar, 
  Package, 
  Users, 
  BarChart3, 
  Plus,
  Eye,
  TrendingUp
} from 'lucide-react'
import EventCreationWizard from '../../components/admin/EventCreationWizard'
import EventManagement from '../../components/admin/EventManagement'
import OrderManagement from '../../components/admin/OrderManagement'
import UserManagement from '../../components/admin/UserManagement'
import Analytics from '../../components/admin/Analytics'
import AuditLogs from '../../components/admin/AuditLogs'
import AdminSubNav from '../../components/admin/AdminSubNav'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

const AdminPage: React.FC = () => {
  const { user, isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalMembers: 0
  })
  const [loading, setLoading] = useState(true)
  const [statsLoaded, setStatsLoaded] = useState(false)

  useEffect(() => {
    console.log('[AdminPage] useEffect triggered. isAdmin:', isAdmin, 'authLoading:', authLoading, 'statsLoaded:', statsLoaded)

    // Don't navigate away during auth loading - prevents flash during tab switch
    if (authLoading) {
      console.log('[AdminPage] Auth still loading, waiting...')
      return
    }

    if (!isAdmin) {
      console.log('[AdminPage] Not admin, navigating to dashboard')
      navigate('/dashboard')
      return
    }

    // Only load stats once, not on every auth state change
    if (statsLoaded) {
      console.log('[AdminPage] Stats already loaded, skipping fetch')
      return
    }

    const loadStats = async () => {
      try {
        console.log('[AdminPage] Loading stats...')
        setLoading(true)

        // Load dashboard stats
        const [eventsResult, ordersResult, usersResult] = await Promise.allSettled([
          supabase.from('secret_events').select('id, status, current_orders'),
          supabase.from('secret_orders').select('id, total_amount, status'),
          supabase.from('secret_users').select('id')
        ])

        const events = eventsResult.status === 'fulfilled' ? eventsResult.value.data || [] : []
        const orders = ordersResult.status === 'fulfilled' ? ordersResult.value.data || [] : []
        const users = usersResult.status === 'fulfilled' ? usersResult.value.data || [] : []

        console.log('[AdminPage] Stats loaded:', {
          events: events.length,
          orders: orders.length,
          users: users.length
        })

        setStats({
          totalEvents: events.length,
          activeEvents: events.filter(e => e.status === 'active').length,
          totalOrders: orders.length,
          totalRevenue: orders
            .filter(o => o.status !== 'cancelled')
            .reduce((sum, o) => sum + (o.total_amount || 0), 0),
          totalMembers: users.length
        })

        setStatsLoaded(true)
      } catch (error) {
        console.error('[AdminPage] Failed to load admin stats:', error)
        setStatsLoaded(true) // Still mark as loaded to prevent infinite retries
      } finally {
        setLoading(false)
        console.log('[AdminPage] Loading complete')
      }
    }

    loadStats()
  }, [isAdmin, authLoading, navigate, statsLoaded])

  if (!isAdmin) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have admin privileges.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe bg-background">
      {/* Admin Sub-Navigation - Always visible on all admin pages */}
      <AdminSubNav />

      <div className="container mx-auto px-4 py-8">
        <Routes>
          <Route path="/" element={<AdminDashboard stats={stats} loading={loading} />} />
          <Route path="/events/new" element={<AdminEventCreation />} />
          <Route path="/events/edit/:eventId" element={<AdminEventEdit />} />
          <Route path="/events" element={<EventManagement />} />
          <Route path="/orders" element={<OrderManagement />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/audit" element={<AuditLogs />} />
        </Routes>
      </div>
    </div>
  )
}

const AdminDashboard: React.FC<{ stats: any; loading: boolean }> = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center space-x-3">
            <LayoutDashboard size={32} className="text-primary" />
            <span>SECRET Admin Dashboard</span>
          </h1>
          <p className="text-muted-foreground mt-2">Manage your exclusive popup events and orders</p>
        </div>
        
        <Link to="/admin/events/new" className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md flex items-center space-x-2 transition-colors">
          <Plus size={16} />
          <span>New Event</span>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <Calendar className="text-primary" size={24} />
            <span className="text-2xl font-bold text-foreground">{stats.totalEvents}</span>
          </div>
          <h3 className="font-medium text-foreground">Total Events</h3>
          <p className="text-muted-foreground text-sm">{stats.activeEvents} active</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <Package className="text-green-600" size={24} />
            <span className="text-2xl font-bold text-foreground">{stats.totalOrders}</span>
          </div>
          <h3 className="font-medium text-foreground">Total Orders</h3>
          <p className="text-muted-foreground text-sm">All time</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="text-blue-600" size={24} />
            <span className="text-2xl font-bold text-foreground">${stats.totalRevenue.toFixed(0)}</span>
          </div>
          <h3 className="font-medium text-foreground">Revenue</h3>
          <p className="text-muted-foreground text-sm">Total earned</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-purple-600" size={24} />
            <span className="text-2xl font-bold text-foreground">{stats.totalMembers}</span>
          </div>
          <h3 className="font-medium text-foreground">Members</h3>
          <p className="text-muted-foreground text-sm">SECRET users</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <BarChart3 className="text-orange-600" size={24} />
            <span className="text-2xl font-bold text-foreground">
              ${stats.totalOrders > 0 ? (stats.totalRevenue / stats.totalOrders).toFixed(0) : '0'}
            </span>
          </div>
          <h3 className="font-medium text-foreground">Avg Order</h3>
          <p className="text-muted-foreground text-sm">Per order value</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link to="/admin/events" className="bg-card border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors group shadow-sm">
          <div className="flex items-center space-x-4">
            <Calendar className="text-primary group-hover:scale-110 transition-transform" size={32} />
            <div>
              <h3 className="font-bold text-foreground">Manage Events</h3>
              <p className="text-muted-foreground text-sm">View and edit popup events</p>
            </div>
          </div>
        </Link>

        <Link to="/admin/orders" className="bg-card border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors group shadow-sm">
          <div className="flex items-center space-x-4">
            <Package className="text-green-600 group-hover:scale-110 transition-transform" size={32} />
            <div>
              <h3 className="font-bold text-foreground">Order Management</h3>
              <p className="text-muted-foreground text-sm">Track and update orders</p>
            </div>
          </div>
        </Link>

        <Link to="/admin/users" className="bg-card border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors group shadow-sm">
          <div className="flex items-center space-x-4">
            <Users className="text-purple-600 group-hover:scale-110 transition-transform" size={32} />
            <div>
              <h3 className="font-bold text-foreground">User Management</h3>
              <p className="text-muted-foreground text-sm">Manage SECRET members</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-bold text-foreground mb-4">Recent Activity</h2>
        <div className="text-center py-8 text-muted-foreground">
          <Eye size={48} className="mx-auto mb-4 opacity-50" />
          <p>Recent activity tracking coming soon...</p>
        </div>
      </div>
    </div>
  )
}

const AdminEventCreation: React.FC = () => {
  const navigate = useNavigate()

  const handleEventCreated = (event: any) => {
    navigate('/admin/events')
  }

  return (
    <div className="space-y-6">
      <EventCreationWizard
        onSuccess={handleEventCreated}
        onCancel={() => navigate('/admin')}
      />
    </div>
  )
}

const AdminEventEdit: React.FC = () => {
  const navigate = useNavigate()
  const { eventId } = useParams<{ eventId: string }>()

  const handleEventUpdated = (event: any) => {
    navigate('/admin/events')
  }

  return (
    <div className="space-y-6">
      <EventCreationWizard
        draftId={eventId}
        onSuccess={handleEventUpdated}
        onCancel={() => navigate('/admin/events')}
      />
    </div>
  )
}

export default AdminPage